import React, { useState, useCallback, memo } from 'react';
import { ImageIcon, Film, Mic2, Scan, Upload, Type, Edit2, Music, FileText, Code, Video, Box, Scissors, Grid3x3, FolderTree } from 'lucide-react';
import { NodeType } from '@/types';

interface NodeTitleProps {
  nodeId: string;
  title: string;
  type: NodeType;
  displayId?: string;
  onUpdate?: (id: string, data: any, size?: any, title?: string) => void;
  className?: string;
}

const NODE_TYPE_ICONS: Record<NodeType, React.ElementType> = {
  [NodeType.PROMPT_INPUT]: Type,
  [NodeType.MEDIA_UPLOAD]: Upload,
  [NodeType.IMAGE_GENERATOR]: ImageIcon,
  [NodeType.VIDEO_GENERATOR]: Film,
  [NodeType.VIDEO_ANALYZER]: Scan,
  [NodeType.IMAGE_EDITOR]: Edit2,
  [NodeType.AUDIO_GENERATOR]: Mic2,
  [NodeType.TEXT_NODE]: Type,
  [NodeType.SCRIPT_NODE]: FileText,
  [NodeType.VIDEO_COMPOSE]: Scissors,
  [NodeType.PANORAMA_GENERATOR]: Grid3x3,
  [NodeType.GROUP]: FolderTree,
};

const NODE_TYPE_NAMES: Record<NodeType, string> = {
  [NodeType.PROMPT_INPUT]: '创意描述',
  [NodeType.MEDIA_UPLOAD]: '媒体上传',
  [NodeType.IMAGE_GENERATOR]: '图片节点',
  [NodeType.VIDEO_GENERATOR]: '视频节点',
  [NodeType.VIDEO_ANALYZER]: '视频分析',
  [NodeType.IMAGE_EDITOR]: '图像编辑',
  [NodeType.AUDIO_GENERATOR]: '音频节点',
  [NodeType.TEXT_NODE]: '文本节点',
  [NodeType.SCRIPT_NODE]: '脚本生成器',
  [NodeType.VIDEO_COMPOSE]: '视频合成',
  [NodeType.PANORAMA_GENERATOR]: '720°全景图',
  [NodeType.GROUP]: '分组',
};

const NodeTitleInner: React.FC<NodeTitleProps> = ({
  nodeId,
  title,
  type,
  displayId,
  onUpdate,
  className = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempTitle, setTempTitle] = useState(title);
  const Icon = NODE_TYPE_ICONS[type] || Box;

  const handleSubmit = useCallback(() => {
    setIsEditing(false);
    if (tempTitle.trim() && tempTitle !== title && onUpdate) {
      onUpdate(nodeId, {}, undefined, tempTitle.trim());
    } else {
      setTempTitle(title);
    }
  }, [nodeId, title, tempTitle, onUpdate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setTempTitle(title);
    }
  }, [handleSubmit, title]);

  const displayTitle = title || NODE_TYPE_NAMES[type];
  const showId = displayId || '';

  return (
    <div
      className={`flex items-center gap-1.5 pointer-events-auto ${className}`}
      style={{ maxWidth: '210px' }}
    >
      <Icon size={12} className="text-slate-400 flex-shrink-0" />
      {isEditing ? (
        <input
          type="text"
          value={tempTitle}
          onChange={(e) => setTempTitle(e.target.value)}
          onBlur={handleSubmit}
          onKeyDown={handleKeyDown}
          className="bg-transparent text-[11px] font-medium text-white outline-none border-b border-cyan-400/50 w-full min-w-0"
          autoFocus
          onMouseDown={(e) => e.stopPropagation()}
        />
      ) : (
        <div
          className="flex items-center gap-1 min-w-0 cursor-default group/title"
          onClick={() => {
            setIsEditing(true);
            setTempTitle(displayTitle);
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span className="text-[11px] font-medium text-slate-300 group-hover/title:text-white transition-colors truncate">
            {displayTitle}
          </span>
          {showId && (
            <span className="text-[10px] text-slate-500 flex-shrink-0">
              {showId}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const NodeTitle = memo(NodeTitleInner, (prevProps, nextProps) => {
  return (
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.title === nextProps.title &&
    prevProps.type === nextProps.type &&
    prevProps.displayId === nextProps.displayId &&
    prevProps.className === nextProps.className
  );
});
